import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

const THIN_BORDER = {
  top: { style: 'thin' },
  left: { style: 'thin' },
  bottom: { style: 'thin' },
  right: { style: 'thin' },
}

const BUDGET_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFDBEDD5' },
}

const YTD_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD0DFE6' },
}

const SINGLES_HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFBFBFBF' },
}

const GOAL_HIGHLIGHT_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFFF00' },
}

const CURRENCY_FMT = '"$"#,##0'
const CURRENCY_DECIMAL_FMT = '"$"#,##0.00'
const PERCENT_FMT = '0%'
const FLEX_GOAL_FMT = '#,##0.00'

const applyCellStyle = (cell, style = {}) => {
  if (style.font) cell.font = { ...cell.font, ...style.font }
  if (style.fill) cell.fill = style.fill
  if (style.alignment)
    cell.alignment = { ...cell.alignment, ...style.alignment }
  if (style.border) cell.border = style.border
  if (style.numFmt) cell.numFmt = style.numFmt
}

const setValue = (ws, address, value, style = {}) => {
  const cell = ws.getCell(address)
  cell.value = value
  applyCellStyle(cell, style)
  return cell
}

const toExcelMoney = (value) => {
  if (value == null) return 'NA'
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 'NA'
}

const repHasGoalHighlight = (rep) =>
  String(rep || '')
    .trim()
    .toLowerCase() === 'partnerships'

export async function exportYoyDifferenceExcel({
  organizationName,
  previousSeasonLabel,
  currentSeasonLabel,
  categoryLabel = 'Ticket Revenue',
  overallPreviousBase,
  totalBudget,
  productRows,
  seasonSummaryNotes,
  salesRepRows,
  getRepGoalValue,
}) {
  const workbook = new ExcelJS.Workbook()
  const ws = workbook.addWorksheet('Sheet1', {
    properties: { defaultColWidth: 10 },
    pageSetup: { showGridLines: true },
  })

  ws.getColumn('A').width = 37.45
  ws.getColumn('B').width = 17.36
  ws.getColumn('C').width = 12.91
  ws.getColumn('D').width = 12.63
  ws.getColumn('E').width = 16.18
  ws.getColumn('F').width = 9.82
  ws.getColumn('G').width = 11.36
  ws.getColumn('J').width = 11.82
  ws.getColumn('M').width = 10.82

  ws.getRow(1).height = 16
  ws.getRow(2).height = 16

  const headerFont = { bold: true, size: 12 }
  const headerStyle = {
    font: headerFont,
    border: THIN_BORDER,
    alignment: { horizontal: 'center', vertical: 'middle' },
  }

  ;[
    ['A1', 'Category'],
    ['B1', 'Products'],
    ['C1', `${previousSeasonLabel} Total`],
    ['D1', `${currentSeasonLabel} Budget`],
    ['E1', 'YOY Difference'],
    ['F1', `${currentSeasonLabel} YTD`],
    ['G1', '% to Budget'],
  ].forEach(([cell, value]) => {
    setValue(ws, cell, value, {
      ...headerStyle,
      fill: ['D1', 'F1'].includes(cell)
        ? cell === 'D1'
          ? BUDGET_FILL
          : YTD_FILL
        : undefined,
    })
  })

  setValue(ws, 'I1', seasonSummaryNotes[0] || '', {
    font: { bold: true, size: 11 },
    border: {
      top: { style: 'thin' },
      left: { style: 'thin' },
    },
  })

  setValue(ws, 'A2', categoryLabel, { font: { bold: true, size: 12 } })
  setValue(ws, 'C2', toExcelMoney(overallPreviousBase), {
    font: { bold: true, size: 12 },
    numFmt: CURRENCY_FMT,
  })
  setValue(ws, 'D2', toExcelMoney(totalBudget), {
    font: { bold: true, size: 12 },
    fill: BUDGET_FILL,
    numFmt: CURRENCY_FMT,
  })
  setValue(
    ws,
    'E2',
    { formula: '1-(C2/D2)' },
    {
      font: { bold: true, size: 12 },
      numFmt: PERCENT_FMT,
    }
  )
  setValue(
    ws,
    'F2',
    { formula: 'SUM(F3:F8)' },
    {
      font: { bold: true, size: 12 },
      fill: YTD_FILL,
      numFmt: CURRENCY_FMT,
    }
  )
  setValue(
    ws,
    'G2',
    { formula: 'F2/D2' },
    {
      font: { bold: true, size: 12 },
      numFmt: PERCENT_FMT,
    }
  )

  seasonSummaryNotes.slice(1).forEach((note, index) => {
    const row = 2 + index
    setValue(ws, `I${row}`, note, {
      font: { size: 11 },
      fill: index === 0 || index === 1 ? YTD_FILL : undefined,
    })
  })

  const productStartRow = 3
  productRows.forEach((row, index) => {
    const rowNum = productStartRow + index
    const ytdValue = toExcelMoney(row.ytd)
    const previousValue = toExcelMoney(row.previousTotal)
    const budgetValue = row.budget == null ? 'NA' : toExcelMoney(row.budget)

    if (row.note) {
      setValue(ws, `A${rowNum}`, row.note, { font: { size: 10 } })
    }

    setValue(ws, `B${rowNum}`, row.product, { font: { size: 11 } })
    setValue(ws, `C${rowNum}`, previousValue, {
      font: { size: 11 },
      alignment: { horizontal: 'right' },
      numFmt: CURRENCY_FMT,
    })
    setValue(ws, `D${rowNum}`, budgetValue, {
      font: { size: 11 },
      fill: BUDGET_FILL,
      alignment: { horizontal: 'right' },
      numFmt: CURRENCY_FMT,
    })

    if (
      row.previousTotal != null &&
      row.budget != null &&
      Number(row.budget) !== 0
    ) {
      setValue(
        ws,
        `E${rowNum}`,
        1 - Number(row.previousTotal) / Number(row.budget),
        {
          font: { size: 11 },
          alignment: { horizontal: 'right' },
          numFmt: PERCENT_FMT,
        }
      )
    }

    setValue(ws, `F${rowNum}`, ytdValue, {
      font: { size: 11 },
      fill: YTD_FILL,
      numFmt: CURRENCY_FMT,
    })

    if (row.budget != null && Number(row.budget) !== 0) {
      setValue(
        ws,
        `G${rowNum}`,
        { formula: `F${rowNum}/D${rowNum}` },
        {
          font: { size: 11 },
          alignment: { horizontal: 'right' },
          numFmt: '0.00%',
        }
      )
    }
  })

  const repHeaderRow = 12
  const repSubHeaderRow = 13
  const repGroupStyle = { font: { bold: true, size: 11 } }

  ;[
    ['B12', 'Season Ticket'],
    ['C12', 'Season Ticket'],
    ['D12', '% to Target'],
    ['E12', 'Flex Packs'],
    ['F12', 'Flex Packs'],
    ['G12', '% to Target'],
    ['H12', 'Groups'],
    ['I12', 'Groups'],
    ['J12', '% to Target'],
    ['K12', 'Singles'],
    ['L12', 'Singles'],
    ['M12', '% to Target'],
  ].forEach(([cell, value]) => {
    setValue(ws, cell, value, {
      ...repGroupStyle,
      fill: ['K12', 'L12', 'M12'].includes(cell)
        ? SINGLES_HEADER_FILL
        : undefined,
    })
  })
  ;[
    ['B13', 'Goal'],
    ['C13', 'Actual'],
    ['E13', 'Goal'],
    ['F13', 'Actual'],
    ['H13', 'Goal'],
    ['I13', 'Actual'],
    ['K13', 'Goal'],
    ['L13', 'Actual'],
  ].forEach(([cell, value]) => {
    setValue(ws, cell, value, {
      ...repGroupStyle,
      fill: ['K13', 'L13'].includes(cell) ? SINGLES_HEADER_FILL : undefined,
    })
  })

  let repRow = 14
  salesRepRows.forEach((row) => {
    setValue(ws, `A${repRow}`, row.rep, { font: { bold: true, size: 11 } })

    const seasonGoal = getRepGoalValue(row.rep, 'seasonGoal')
    const flexGoal = getRepGoalValue(row.rep, 'flexGoal')
    const groupsGoal = getRepGoalValue(row.rep, 'groupsGoal')
    const singlesGoal = getRepGoalValue(row.rep, 'singlesGoal')
    const highlightGoals = repHasGoalHighlight(row.rep)

    setValue(ws, `B${repRow}`, seasonGoal ?? 0, {
      font: { size: 11 },
      fill: highlightGoals ? GOAL_HIGHLIGHT_FILL : undefined,
      numFmt: CURRENCY_FMT,
    })
    setValue(ws, `C${repRow}`, row.seasonActual ?? 0, {
      font: { size: 11 },
      numFmt: CURRENCY_FMT,
    })
    setValue(
      ws,
      `D${repRow}`,
      { formula: `IF(B${repRow}=0,"",C${repRow}/B${repRow})` },
      { font: { size: 11 }, numFmt: PERCENT_FMT }
    )

    setValue(ws, `E${repRow}`, flexGoal ?? 0, {
      font: { size: 11 },
      fill: highlightGoals ? GOAL_HIGHLIGHT_FILL : undefined,
      numFmt: FLEX_GOAL_FMT,
    })
    setValue(ws, `F${repRow}`, row.flexActual ?? 0, {
      font: { size: 11 },
      numFmt: CURRENCY_FMT,
    })
    setValue(
      ws,
      `G${repRow}`,
      { formula: `IF(E${repRow}=0,"",F${repRow}/E${repRow})` },
      { font: { size: 11 }, numFmt: PERCENT_FMT }
    )

    setValue(ws, `H${repRow}`, groupsGoal ?? 0, {
      font: { size: 11 },
      fill: highlightGoals ? GOAL_HIGHLIGHT_FILL : undefined,
      numFmt: CURRENCY_FMT,
    })
    setValue(ws, `I${repRow}`, row.groupsActual ?? 0, {
      font: { size: 11 },
      numFmt: CURRENCY_FMT,
    })
    setValue(
      ws,
      `J${repRow}`,
      { formula: `IF(H${repRow}=0,"",I${repRow}/H${repRow})` },
      { font: { size: 11 }, numFmt: PERCENT_FMT }
    )

    const singlesFmt =
      row.rep?.toLowerCase() === 'online' ? CURRENCY_DECIMAL_FMT : CURRENCY_FMT

    setValue(ws, `K${repRow}`, singlesGoal ?? 0, {
      font: { size: 11 },
      numFmt: singlesFmt,
    })
    setValue(ws, `L${repRow}`, row.singlesActual ?? 0, {
      font: { size: 11 },
      numFmt: singlesFmt,
    })
    setValue(
      ws,
      `M${repRow}`,
      { formula: `IF(K${repRow}=0,"",L${repRow}/K${repRow})` },
      { font: { size: 11 }, numFmt: PERCENT_FMT }
    )

    repRow += 2
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const safeOrgName = String(organizationName || 'Organization').replace(
    /[\\/:*?"<>|]/g,
    ''
  )
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `${safeOrgName} Ticket Report.xlsx`
  )
}
